// apps/web/src/pages/author/constraint-config/PredicateBoundsCounter.tsx
//
// Phase 9 Plan 08 Task 1 — Live bounds counter for the Predicate AST editor.
//
// Subscribes to the root predicate via useWatch (per-path, NEVER top-level —
// authr-06) and renders the iterative DEPTH/BREADTH/NODES counts. The output
// is text-muted-foreground while in bounds and text-destructive when any axis
// is over its bound. The aria-live="polite" output lets SR users hear the
// counts update as they edit.
//
// computePredicateStats is ITERATIVE (REVIEWS-3 MEDIUM-13 idiom — mirrors
// packages/shared/src/schemas/constraint.ts countPredicateNodes lines 162-188
// and the prewalk in lines 190-228). NO recursion: a worklist queue of
// [node, depth] pairs walks the tree breadth-first; depth = max depth seen;
// breadth = max predicates.length seen across all groups; nodes = total count.
//
// Bounds (per packages/shared/src/schemas/constraint.ts):
//   DEPTH ≤ 8
//   BREADTH ≤ 10  (predicates.length cap on any single group)
//   NODES ≤ 100   (total node count in the tree)
//
// UI-SPEC §"Selectability" copy table (lines 503-516):
//   "DEPTH {d}/8 — BREADTH {b}/10 — NODES {n}/100"
//   text-muted-foreground in bounds; text-destructive when any axis over.

import type { Predicate } from "@the-recipe-spread/shared";
import type * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { cn } from "@/lib/utils";

const MAX_DEPTH = 8;
const MAX_BREADTH = 10;
const MAX_NODES = 100;

export interface PredicateStats {
  depth: number;
  breadth: number;
  nodes: number;
}

// Iterative walker — explicit worklist queue of [node, depth] tuples. Mirrors
// the shared package's countPredicateNodes / checkPredicateDepthPrewalk
// pattern. NO recursion (REVIEWS-3 MEDIUM-13 + REVIEWS-4 HIGH-2 invariants).
export function computePredicateStats(root: unknown): PredicateStats {
  if (root === null || typeof root !== "object" || !("kind" in root)) {
    return { depth: 0, breadth: 0, nodes: 0 };
  }

  let maxDepth = 0;
  let maxBreadth = 0;
  let nodes = 0;

  const queue: Array<[unknown, number]> = [[root, 0]];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined) continue;
    const [node, depth] = next;
    if (node === null || typeof node !== "object" || !("kind" in node)) continue;

    nodes += 1;
    if (depth > maxDepth) maxDepth = depth;

    const k = (node as { kind: string }).kind;
    if (k === "and" || k === "or") {
      const children = (node as { predicates?: unknown[] }).predicates;
      if (Array.isArray(children)) {
        if (children.length > maxBreadth) maxBreadth = children.length;
        for (const child of children) {
          queue.push([child, depth + 1]);
        }
      }
    }
    // Defensive early-exit: a wildly over-cap tree could grow the queue. The
    // Phase 01.1 schema enforces 100-node cap at parse time; the UI's bounds
    // counter just reports the over-cap status. Cap iteration at 10× the
    // node bound to ensure no pathological cost during editing.
    if (nodes > MAX_NODES * 10) break;
  }

  return { depth: maxDepth, breadth: maxBreadth, nodes };
}

export interface PredicateBoundsCounterProps {
  /** RHF form path to the root predicate node, e.g. "predicate" or
   * "constraints.0.predicate". */
  predicatePath: string;
}

export function PredicateBoundsCounter({
  predicatePath,
}: PredicateBoundsCounterProps): React.ReactElement {
  const { control } = useFormContext();
  // Per-path useWatch ONLY — never top-level. The watched value can be any
  // shape during edits (RHF passes through partial values during typing);
  // computePredicateStats is defensive.
  const root = useWatch({ control, name: predicatePath }) as Predicate | undefined;
  const stats = computePredicateStats(root);

  const overDepth = stats.depth > MAX_DEPTH;
  const overBreadth = stats.breadth > MAX_BREADTH;
  const overNodes = stats.nodes > MAX_NODES;
  const overBound = overDepth || overBreadth || overNodes;

  return (
    <output
      data-testid="predicate-bounds-counter"
      aria-live="polite"
      className={cn(
        "text-xs font-mono tracking-tight",
        overBound ? "text-destructive font-semibold" : "text-muted-foreground",
      )}
    >
      DEPTH {stats.depth}/{MAX_DEPTH} — BREADTH {stats.breadth}/{MAX_BREADTH} — NODES {stats.nodes}/
      {MAX_NODES}
    </output>
  );
}
PredicateBoundsCounter.displayName = "PredicateBoundsCounter";
